import ChevronBottom from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  Animated,
  Easing,
  SectionList,
  TouchableOpacity,
  View,
} from 'react-native';
import { Badge, BadgeSize } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import { formatDateWithYear } from '@/utils/date';
import { ItemDetail_PurchaseHistoryQuery } from '@/types';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { SectionEmptyState } from '@usepepper/ui/atoms/section-empty-state';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface ItemHistorySection {
  data: ItemDetail_PurchaseHistoryQuery['getVariantPackPurchaseHistory'];
  key: string;
}

interface Props {
  itemPurchaseHistoryData: ItemDetail_PurchaseHistoryQuery['getVariantPackPurchaseHistory'];
  loading: boolean;
  onPress: (open: boolean) => void;
}

export const ItemPurchaseHistoryList = ({
  itemPurchaseHistoryData,
  loading,
  onPress,
}: Props) => {
  const [styles, theme] = useStyles();

  const hasChevronAnimated = useRef(false);
  const animatedValueRef = useRef(new Animated.Value(0));
  const itemPurchaseHistoryContentHeightRef = useRef(0);
  const [shouldShowItemPurchaseHistory, setShouldShowItemPurchaseHistory] =
    useState(false);

  // chevron animation
  useEffect(() => {
    if (hasChevronAnimated.current) {
      Animated.timing(animatedValueRef.current, {
        duration: 250,
        easing: Easing.out(Easing.exp),
        toValue: shouldShowItemPurchaseHistory ? 1 : 0,
        useNativeDriver: false,
      }).start();
    } else {
      hasChevronAnimated.current = true;
    }
  }, [shouldShowItemPurchaseHistory]);

  // section item purchase history entries by fulfillment date
  const itemPurchaseHistorySections: ItemHistorySection[] = useMemo(() => {
    const history = itemPurchaseHistoryData;

    if (!history) {
      return [];
    }

    const itemPurchaseHistoryMap = history.reduce(
      (
        acc: Record<string, ItemHistorySection>,
        item: ItemDetail_PurchaseHistoryQuery['getVariantPackPurchaseHistory'][number],
      ) => {
        const date = item.fulfillment_date;

        if (!date) {
          return acc;
        }

        if (!acc[date]) {
          acc[date] = { data: [], key: date };
        }

        acc[date].data.push(item);
        return acc;
      },
      {} as Record<string, ItemHistorySection>,
    );

    return Object.values(itemPurchaseHistoryMap);
  }, [itemPurchaseHistoryData]);

  return (
    <View style={styles.container}>
      <TouchableOpacity
        onPress={() => {
          setShouldShowItemPurchaseHistory(!shouldShowItemPurchaseHistory);
          onPress(!shouldShowItemPurchaseHistory);
        }}
        style={styles.header}
      >
        <Typography
          color={theme.sk.color.text.tertiary}
          variant="paragraph-md-600"
        >
          {i18n.t('fulfillment_history')}
        </Typography>
        <Animated.View
          style={{
            transform: [
              {
                rotate: animatedValueRef.current.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '-180deg'],
                }),
              },
            ],
          }}
        >
          <ChevronBottom fill={theme.sk.color.icon.tertiary} width={12} />
        </Animated.View>
      </TouchableOpacity>
      <Animated.View
        style={[
          styles.animatedListContainer,
          {
            height: animatedValueRef.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, itemPurchaseHistoryContentHeightRef.current],
            }),
          },
        ]}
      >
        <View
          onLayout={(e) => {
            itemPurchaseHistoryContentHeightRef.current =
              e.nativeEvent.layout.height;
          }}
          style={styles.listContainer}
        >
          {loading ? (
            <View
              accessibilityState={{
                busy: true,
              }}
            >
              <ActivityIndicator
                accessibilityLabel={i18n.t('item_purchase_history.loading')}
                size={32}
              />
            </View>
          ) : (
            <SectionList
              contentContainerStyle={styles.list}
              keyExtractor={(item, index) =>
                [
                  'item-purchase-history-list-item',
                  index.toString(),
                  item.fulfillment_date,
                  item.order_date,
                  item.unit,
                  item.unit_count,
                ]
                  .filter(Boolean)
                  .join('-')
              }
              ListEmptyComponent={
                <View style={styles.emptyStateContainer}>
                  <SectionEmptyState
                    text={i18n.t(
                      'item_purchase_history.no_purchase_history.title',
                    )}
                  />
                </View>
              }
              renderItem={({ item }) => {
                return (
                  <ListItem
                    leftElement={
                      item.unit_count ? (
                        <View style={styles.badgeContainer}>
                          <Badge
                            label={item.quantity?.toString() ?? ''}
                            size={BadgeSize.MEDIUM}
                            style={styles.badge}
                          />
                        </View>
                      ) : null
                    }
                    title={
                      <Typography
                        color={theme.sk.color.text.primary}
                        variant="paragraph-md"
                      >
                        {item.unit}
                      </Typography>
                    }
                  />
                );
              }}
              renderSectionHeader={({ section }) => (
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md-600"
                >
                  {formatDateWithYear(section.key)}
                </Typography>
              )}
              scrollEnabled={false}
              sections={itemPurchaseHistorySections}
            />
          )}
        </View>
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  animatedListContainer: {
    overflow: 'hidden',
  },
  badge: {
    borderColor: theme.sk.color.border.secondary,
    borderRadius: 4,
    borderWidth: 1,
  },
  badgeContainer: {
    alignSelf: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  container: {
    gap: 14,
  },
  emptyStateContainer: {
    paddingHorizontal: 64,
    paddingVertical: 24,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  list: {
    gap: 8,
  },
  listContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
}));
